import React, { useRef, useState } from "react";
import {
  SearchWrapper,
  SearchInputField,
  IconWrapper,
  ClearIconWrapper,
  SearchFieldLabel,
} from "./SearchInput.styles";
import { SearchInputProps } from "./SearchInput.types";
import Flex from "../../primitives/Flex/Flex";
import { Body3RegularTypography } from "../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../theme/colors";
import { Icon } from "../Icon/Icon";
import { SearchInputAnimatedKeywords } from "./SearchInputAnimatedKeywords";

const DEFAULT_KEYWORDS = [
  "'Myntra Clothes'",
  "'Pants for men'",
  "'Meesho'",
  "'Dresses for women'",
];
const HINT_COLOR = "text.default.secondary";

export const SearchInput: React.FC<SearchInputProps> = ({
  value = "",
  variant = "default",
  showClearButton = true,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  label = "",
  placeholderPrefix = "Search for",
  keywords = DEFAULT_KEYWORDS,
  rotateIntervalMs,
  onClick,
  onClickClearIcon,
  hideHint = false,
  dataCy,
  className,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const showHint = !hideHint && !value && !isFocused;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange({ ...e, target: { ...e.target, value: newValue } });
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleWrapperClick = () => {
    inputRef.current?.focus();
    onClick?.();
  };

  const hintAriaLabel =
    variant === "animatedKeywords"
      ? `${placeholderPrefix} ${keywords[0] ?? ""}`.trim()
      : label;

  return (
    <SearchWrapper
      className={className}
      isFocused={isFocused}
      hasValue={!!value}
      onClick={handleWrapperClick}
    >
      <IconWrapper>
        <Icon
          name="fa-solid fa-magnifying-glass"
          color={HINT_COLOR}
          height="16"
          width="16"
          size="16"
        />
      </IconWrapper>
      {showHint && variant === "default" && label ? (
        <SearchFieldLabel>
          <Body3RegularTypography color={HINT_COLOR}>
            {label}
          </Body3RegularTypography>
        </SearchFieldLabel>
      ) : null}
      {showHint && variant === "animatedKeywords" ? (
        <SearchFieldLabel aria-hidden>
          <Flex
            as="span"
            inline
            alignItems="center"
            minWidth={0}
            overflow="hidden"
            whiteSpace="nowrap"
          >
            <Body3RegularTypography color={HINT_COLOR} flexShrink={0}>
              {placeholderPrefix}&nbsp;
            </Body3RegularTypography>
            <SearchInputAnimatedKeywords
              keywords={keywords}
              intervalMs={rotateIntervalMs}
              color={HINT_COLOR}
            />
          </Flex>
        </SearchFieldLabel>
      ) : null}
      <SearchInputField
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        aria-label={showHint ? hintAriaLabel : undefined}
        data-cy={dataCy}
      />
      {showClearButton && (isFocused || value) ? (
        <ClearIconWrapper
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onClickClearIcon?.();
          }}
        >
          <Icon
            name="fa-solid fa-xmark"
            color={colorPalette.gray[600]}
            size="14"
            height="16"
            width="16"
          />
        </ClearIconWrapper>
      ) : null}
    </SearchWrapper>
  );
};

export default SearchInput;
